// Wallet event monitoring and logging hook for the AlphaMind IDO Frontend
import { useEffect, useCallback, useRef } from 'react';
import { useAccount, useDisconnect } from 'wagmi';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { useLogger, useWalletConnectionLogger } from './useLogger';
import { WalletType } from '../types/logging.types';

interface WalletEventLoggerConfig {
  enablePeriodicFlush?: boolean;
  flushInterval?: number;
  enableDebugLogs?: boolean;
}

export const useWalletEventLogger = (config: WalletEventLoggerConfig = {}) => {
  const {
    enablePeriodicFlush = true,
    flushInterval = 5000, // 5 seconds
    enableDebugLogs = false
  } = config;

  const logger = useLogger();
  const walletLogger = useWalletConnectionLogger();
  
  // Ethereum wallet state
  const { address: ethAddress, isConnected: ethConnected, connector } = useAccount();
  const { disconnect } = useDisconnect();
  
  // Solana wallet state
  const { 
    publicKey: solanaPublicKey, 
    connected: solanaConnected, 
    wallet: solanaWallet,
    disconnect: disconnectSolana,
    connecting: solanaConnecting,
    disconnecting: solanaDisconnecting
  } = useSolanaWallet();

  // Previous state tracking
  const prevStateRef = useRef({
    ethConnected: false,
    ethAddress: null as string | null,
    solanaConnected: false,
    solanaAddress: null as string | null,
    solanaWalletName: null as string | null
  });

  const periodicFlushRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Force flush logs after wallet events
  const forceFlushLogs = useCallback(async () => {
    try {
      if (logger.hasPendingLogs()) {
        if (enableDebugLogs) {
          console.log('[WalletEventLogger] Forcing flush of pending logs');
        }
        await logger.forceFlush();
      }
    } catch (error) {
      console.error('[WalletEventLogger] Error forcing log flush:', error);
    }
  }, [logger, enableDebugLogs]);

  // Log wallet state change with comprehensive data
  const logWalletStateChange = useCallback(async (
    walletType: WalletType,
    action: string,
    data: Record<string, any>
  ) => {
    try {
      // Log the wallet event
      await logger.logWalletConnection(walletType, action as any, {
        ...data,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });

      // Force flush immediately after wallet events
      setTimeout(forceFlushLogs, 100);
    } catch (error) {
      console.error('[WalletEventLogger] Error logging wallet state change:', error);
    }
  }, [logger, forceFlushLogs]);

  // Monitor Ethereum wallet changes
  useEffect(() => {
    const prevState = prevStateRef.current;
    const currentEthAddress = ethAddress?.toLowerCase() || null;
    
    // Ethereum connection state changed
    if (prevState.ethConnected !== ethConnected) {
      if (ethConnected && currentEthAddress) {
        // Wallet connected
        logWalletStateChange('metamask', 'wallet_connected', {
          address: currentEthAddress,
          connector: connector?.name || 'unknown',
          chainId: Array.isArray(connector?.chains) ? connector.chains[0]?.id : undefined,
          previouslyConnected: prevState.ethConnected
        });
        
        walletLogger.logConnectionSuccess('metamask', {
          address: currentEthAddress,
          connector: connector?.name
        });
      } else if (!ethConnected && prevState.ethConnected) {
        // Wallet disconnected
        logWalletStateChange('metamask', 'wallet_disconnected', {
          previousAddress: prevState.ethAddress,
          connector: connector?.name || 'unknown',
          wasConnected: prevState.ethConnected
        });
      }
    }

    // Ethereum address changed (wallet switched)
    if (prevState.ethAddress !== currentEthAddress && ethConnected) {
      logWalletStateChange('metamask', 'wallet_switched', {
        previousAddress: prevState.ethAddress,
        newAddress: currentEthAddress,
        connector: connector?.name || 'unknown'
      });
    }

    // Update previous state
    prevState.ethConnected = ethConnected;
    prevState.ethAddress = currentEthAddress;
  }, [ethConnected, ethAddress, connector, logWalletStateChange, walletLogger]);

  // Monitor Solana wallet changes
  useEffect(() => {
    const prevState = prevStateRef.current;
    const currentSolanaAddress = solanaPublicKey?.toString() || null;
    const currentWalletName = solanaWallet?.adapter?.name || null;
    
    // Solana connection state changed
    if (prevState.solanaConnected !== solanaConnected) {
      if (solanaConnected && currentSolanaAddress) {
        // Solana wallet connected
        logWalletStateChange('phantom', 'wallet_connected', {
          address: currentSolanaAddress,
          walletName: currentWalletName,
          walletIcon: solanaWallet?.adapter?.icon,
          walletUrl: solanaWallet?.adapter?.url,
          previouslyConnected: prevState.solanaConnected
        });
        
        walletLogger.logConnectionSuccess('phantom', {
          address: currentSolanaAddress,
          walletName: currentWalletName
        });
      } else if (!solanaConnected && prevState.solanaConnected) {
        // Solana wallet disconnected
        logWalletStateChange('phantom', 'wallet_disconnected', {
          previousAddress: prevState.solanaAddress,
          previousWalletName: prevState.solanaWalletName,
          wasConnected: prevState.solanaConnected
        });
      }
    }

    // Solana address changed (wallet switched)
    if (prevState.solanaAddress !== currentSolanaAddress && solanaConnected) {
      logWalletStateChange('phantom', 'wallet_switched', {
        previousAddress: prevState.solanaAddress,
        newAddress: currentSolanaAddress,
        walletName: currentWalletName
      });
    }

    // Solana wallet changed (different wallet app)
    if (prevState.solanaWalletName !== currentWalletName && solanaConnected) {
      logWalletStateChange('phantom', 'wallet_changed', {
        previousWallet: prevState.solanaWalletName,
        newWallet: currentWalletName,
        address: currentSolanaAddress
      });
    }

    // Update previous state
    prevState.solanaConnected = solanaConnected;
    prevState.solanaAddress = currentSolanaAddress;
    prevState.solanaWalletName = currentWalletName;
  }, [solanaConnected, solanaPublicKey, solanaWallet, logWalletStateChange, walletLogger]);

  // Monitor Solana connecting/disconnecting states
  useEffect(() => {
    if (solanaConnecting) {
      logWalletStateChange('phantom', 'wallet_connecting', {
        walletName: solanaWallet?.adapter?.name,
        timestamp: Date.now()
      });
    }
  }, [solanaConnecting, solanaWallet, logWalletStateChange]);

  useEffect(() => {
    if (solanaDisconnecting) {
      logWalletStateChange('phantom', 'wallet_disconnecting', {
        walletName: solanaWallet?.adapter?.name,
        address: solanaPublicKey?.toString(),
        timestamp: Date.now()
      });
    }
  }, [solanaDisconnecting, solanaWallet, solanaPublicKey, logWalletStateChange]);

  // Periodic flush to ensure logs are sent
  useEffect(() => {
    if (!enablePeriodicFlush) return;

    periodicFlushRef.current = setInterval(async () => {
      if (logger.hasPendingLogs()) {
        if (enableDebugLogs) {
          console.log('[WalletEventLogger] Periodic flush check - pending logs found');
        }
        await forceFlushLogs();
      }
    }, flushInterval);

    return () => {
      if (periodicFlushRef.current) {
        clearInterval(periodicFlushRef.current);
        periodicFlushRef.current = null;
      }
    };
  }, [enablePeriodicFlush, flushInterval, forceFlushLogs, logger, enableDebugLogs]);

  // Listen for page visibility changes to flush logs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (enableDebugLogs) {
          console.log('[WalletEventLogger] Page hidden - forcing log flush');
        }
        forceFlushLogs();
      }
    };

    const handleBeforeUnload = () => {
      if (enableDebugLogs) {
        console.log('[WalletEventLogger] Page unloading - forcing log flush');
      }
      forceFlushLogs();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [forceFlushLogs, enableDebugLogs]);

  // Return utility functions
  return {
    forceFlushLogs,
    hasPendingLogs: () => logger.hasPendingLogs(),
    getCurrentWalletState: () => ({
      ethereum: {
        connected: ethConnected,
        address: ethAddress,
        connector: connector?.name
      },
      solana: {
        connected: solanaConnected,
        address: solanaPublicKey?.toString(),
        walletName: solanaWallet?.adapter?.name
      }
    }),
    // Manual logging functions for custom events
    logCustomWalletEvent: logWalletStateChange
  };
};
